import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Phone, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useLanguage } from '../context/LanguageContext';

const slides = [
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/073da548-8b42-4c53-baa6-6ea4ddd4386f/images/5a8a8fb87da83d541caea99dc39e4a309812d375cdbc49e58ef9939086f82a74.jpeg',
    titleFr: 'Refusé par votre banque\u00a0?',
    titleEn: 'Refused by Your Bank?',
    subtitleFr: 'Découvrez vos options de financement auto.',
    subtitleEn: 'Discover your auto financing options.',
    descFr: 'Mauvais crédit, faillite, proposition de consommateur — notre équipe analyse votre situation et vous présente les options disponibles.',
    descEn: 'Bad credit, bankruptcy, consumer proposal — our team analyzes your situation and presents available options.'
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/073da548-8b42-4c53-baa6-6ea4ddd4386f/images/a3be3d048ab428512902863540a86d4ab99b23c59d63d1dcb366d4c2362aae50.jpeg',
    titleFr: 'Votre financement auto',
    titleEn: 'Your Auto Financing',
    subtitleFr: 'adapté à votre situation.',
    subtitleEn: 'adapted to your situation.',
    descFr: 'Des milliers de clients nous ont fait confiance. Même avec un crédit difficile, nos experts trouvent des solutions rapides et efficaces.',
    descEn: 'Thousands of clients have trusted us. Even with difficult credit, our experts find fast and effective solutions.'
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/073da548-8b42-4c53-baa6-6ea4ddd4386f/images/35816b756e59ac0b56404a501eeaf88cba1da3ff065ec55c4e15b4754b475565.jpeg',
    titleFr: 'Reprenez la route',
    titleEn: 'Get Back on the Road',
    subtitleFr: 'en toute confiance.',
    subtitleEn: 'with confidence.',
    descFr: 'Un véhicule fiable, un financement structuré et un accompagnement du début à la fin. Sans jugement, sans obligation.',
    descEn: 'A reliable vehicle, structured financing and support from start to finish. No judgment, no obligation.'
  }
];

const HeroCarousel = () => {
  const { language, t } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('next');

  const goTo = useCallback((idx, dir = 'next') => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setTimeout(() => setAnimating(false), 50);
    }, 400);
  }, [animating]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length, 'next');
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length, 'prev');
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className="relative min-h-[560px] md:min-h-[700px] flex items-center justify-center overflow-hidden pt-24 md:pt-32" data-testid="hero-carousel">
      {/* Background images — preload all, show current */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 z-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b130e]/85 via-[#0b130e]/50 to-transparent z-10" />
          <img
            src={s.image}
            alt=""
            className="w-full h-full object-cover object-center"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-20 w-full">
        <div className="max-w-3xl">
          <Badge className="bg-[#1a5c3b] hover:bg-[#165432] text-white mb-4 md:mb-6 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-semibold" data-testid="hero-badge">
            <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 md:mr-2" />
            {language === 'en' ? '2nd & 3rd Chance Auto Financing' : 'Financement auto 2e et 3e chance'}
          </Badge>

          <div
            className={`transition-all duration-500 ${
              animating
                ? `opacity-0 ${direction === 'next' ? 'translate-y-8' : '-translate-y-8'}`
                : 'opacity-100 translate-y-0'
            }`}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-2 leading-[1.05]">
              {language === 'en' ? slide.titleEn : slide.titleFr}
            </h1>
            <p className="text-lg md:text-3xl lg:text-4xl font-medium text-green-400 mb-6">
              {language === 'en' ? slide.subtitleEn : slide.subtitleFr}
            </p>
            <p className="hidden md:block text-base md:text-lg text-gray-300 mb-10 leading-relaxed max-w-2xl">
              {language === 'en' ? slide.descEn : slide.descFr}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 flex-wrap items-start">
            <Link to="/demander-un-pret-auto" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-[#1a5c3b] hover:bg-[#165432] text-white px-8 md:px-10 py-5 md:py-6 text-sm md:text-lg font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 uppercase tracking-wide" data-testid="hero-cta-primary">
                {language === 'en' ? 'APPLY NOW' : 'FAIRE UNE DEMANDE'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+15144183968" className="hidden sm:block">
              <Button size="lg" variant="outline" className="border-2 border-white/70 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold transition-all duration-300 uppercase tracking-wide" data-testid="hero-cta-secondary">
                <Phone className="mr-2 w-5 h-5" />
                {language === 'en' ? 'SPEAK TO AN ADVISOR' : 'PARLER À UN CONSEILLER'}
              </Button>
            </a>
          </div>
          <p className="mt-4 md:mt-5 text-xs md:text-sm text-green-200/80 font-medium tracking-wide">
            {t.hero.trustLine}
          </p>
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 text-white transition-all hidden md:flex"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-3 text-white transition-all hidden md:flex"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i, i > current ? 'next' : 'prev')}
            className={`rounded-full transition-all duration-300 ${
              i === current ? 'w-8 h-2.5 bg-[#1a5c3b]' : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroCarousel;
